import {
  assignmentsResponseSchema,
  homeworkAssignmentResponseSchema,
  type StudentProblemSnapshot,
} from '@wx-weiqi/contracts';
import type { IdentityStore } from '../stores/identity-store';

export interface StudentPracticeAssignment {
  readonly assignmentId: string;
  readonly title: string;
}

export interface StudentPracticeClient {
  listAssignments(): Promise<readonly StudentPracticeAssignment[]>;
  loadProblems(assignmentId: string): Promise<readonly StudentProblemSnapshot[]>;
}

/** Independent read-only source: opening a practice diagram never starts an answer session. */
export class ServiceStudentPracticeClient implements StudentPracticeClient {
  constructor(private readonly identity: Pick<IdentityStore, 'authorized'>) {}

  async listAssignments(): Promise<readonly StudentPracticeAssignment[]> {
    const assignments = new Map<string, StudentPracticeAssignment>();
    const seenCursors = new Set<string>();
    let after: string | null = null;
    for (let page = 0; page < 100; page += 1) {
      const query = new URLSearchParams({ limit: '50' });
      if (after !== null) query.set('after', after);
      const response = await this.identity.authorized(
        `/me/homework-assignments?${query.toString()}`,
        assignmentsResponseSchema,
      );
      for (const assignment of response.data.assignments) {
        assignments.set(assignment.assignmentId, {
          assignmentId: assignment.assignmentId,
          title: assignment.title,
        });
      }
      const nextAfter = response.data.nextAfter;
      if (nextAfter === null) return [...assignments.values()];
      if (seenCursors.has(nextAfter)) throw new Error('作业列表加载不完整，请重新打开。');
      seenCursors.add(nextAfter);
      after = nextAfter;
    }
    throw new Error('作业列表过长，请稍后重试。');
  }

  async loadProblems(assignmentId: string): Promise<readonly StudentProblemSnapshot[]> {
    // This endpoint checks assignment ownership and organization, and never returns solutions.
    const response = await this.identity.authorized(
      `/homework-assignments/${encodeURIComponent(assignmentId)}`,
      homeworkAssignmentResponseSchema,
    );
    const assignment = response.data.assignment;
    if (assignment.assignmentId !== assignmentId) {
      throw new Error('作业信息不匹配，请重新选择。');
    }
    return [...assignment.items]
      .sort((left, right) => left.sortOrder - right.sortOrder)
      .map((item) => item.problem);
  }
}
